import React, { useContext, memo, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import queryString from 'query-string-for-all';

import { AppContext } from 'src/components/app-context';
import { Panel } from '@prisjakt/ui/panel';
import { Translate } from 'src/components/translate';
import { NumberPagination } from 'src/components/number-pagination';
import { ScrollLink, ScrollElement } from 'src/components/scroll';
import { ProductImage } from 'src/components/product-image';
import CampaignBanner from '../../../campaign/components/banner';
import { Lazy } from 'src/components/lazy';
import AdContainer from '../../../../components/ads';
import ProductListingHeader from '../header';
import CampaignBadge from '../../../../components/campaign-badge';
import Product from './components/product';
import { useActiveCampaignByProductId } from '../../../../hooks/active-campaign-by-product-id';

import type { AppState } from 'src/reducers';

import type {
  CategoryProductListingFragment,
  CategoryPageQuery,
  Campaign,
} from '../../../../types';
import type { DeepNonNullable } from '../../types';
import type { ProductSelectedToCompare } from 'src/hooks/compare-products-selection';
import {
  ProductListingInformation,
  ProductListingItem,
  ProductListingTableBody,
  ProductListingWrapper,
  ProductListingFooter,
} from './styles';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

const SCROLL_KEY = 'Product Listing';

const addStickyOffset = () => {
  if (window.innerWidth < 640) {
    return 50;
  }

  return 70;
};

type ProductProperties =
  DeepNonNullable<CategoryProductListingFragment>['products']['nodes'][number]['properties']['nodes'];

const getSelectedProductProperties = (
  properties: ProductProperties,
  selectedProperties: { id: string; name: string }[]
) => {
  if (
    !properties ||
    properties.length === 0 ||
    !selectedProperties ||
    selectedProperties.length === 0
  ) {
    return [];
  }

  const selectedIds = selectedProperties.map(x => x.id);

  // Sort properties into the correct order to match headers
  return properties
    .filter(x => selectedIds.includes(x.id))
    .sort((a, b) => selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id));
};

const getBadge = (
  campaign: Campaign,
  activeCampaign: Campaign,
  viewMode: 'table' | 'grid' | 'row'
) => {
  if (!campaign) {
    return undefined;
  }

  return viewMode === 'table' ? (
    // <CampaignBadge campaign={campaign} />
    <CampaignBadge colors={undefined} />
  ) : (
    <CampaignBanner type="banner_logo_big" banners={activeCampaign.banners} />
  );
};

const getPriceBySortMode = (
  product: CategoryProduct,
  sortMode: PageInfo['sort']
) => {
  const defaultPrice =
    product.priceSummary.regular || product.priceSummary.alternative;

  if (!sortMode.name) {
    return defaultPrice;
  }

  switch (sortMode.name) {
    case 'price':
      return defaultPrice;
    case 'price_include_shipping':
      return product.priceSummary.includeShipping || defaultPrice;
    default:
      return defaultPrice;
  }
};

type PageInfo = {
  offset: number;
  limit: number;
  hasNext: boolean;
  hasPrevious: boolean;
  sort: {
    name: string;
    direction: 'asc' | 'desc';
    available: string[];
  };
};

type Products = DeepNonNullable<CategoryPageQuery>['category']['products'];

type CategoryProduct = Products['nodes'][number];

type ProductListingProps = {
  children?: React.ReactNode;
  withFilter: boolean;
  pageInfo: PageInfo;
  header: React.ReactNode;
  mode: 'table' | 'row' | 'grid';
  products: Products;
  propertySummaryMode: string;
  selectedProperties: { id: string; name: string }[];
  onSortChange?: (key: string) => void;
  fallbackImage?: string;
  showPagination?: boolean;
  showRatings?: boolean;
  showCategory?: boolean;
  showProductSelector?: boolean;
  feedbackPrompt?: React.ReactNode;
  feedbackPromptPosition?: number;
  onProductSelect?: (id: number, type: string) => void;
  onProductDeselect?: (id: number, type: string) => void;
  onProductClicked?: (index: number) => void;
  selectedProducts?: ProductSelectedToCompare[];
  adverts?: {
    desktopInline?: React.ReactElement;
    mobileInline1?: React.ReactElement;
    mobileInline2?: React.ReactElement;
  };
  productCount: number;
  className?: string;
};

const ProductListing: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<ProductListingProps>>
> = ({
  children,
  header,
  products,
  pageInfo,
  mode,
  selectedProperties,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSortChange = () => {},
  fallbackImage,
  showPagination = true,
  showRatings = false,
  showCategory = false,
  showProductSelector = false,
  feedbackPrompt,
  feedbackPromptPosition,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onProductSelect = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onProductDeselect = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onProductClicked = () => {},
  selectedProducts = [],
  adverts = {},
  productCount,
  className,
}) => {
  const { t, market } = useContext(AppContext);
  const { search } = useLocation();
  const { nodes } = products;

  const productIds = useMemo(() => nodes.map(product => product.id), [nodes]);
  const productsWithActiveCampaign = useActiveCampaignByProductId(productIds);

  const activeCampaign = useSelector((state: AppState) =>
    (state.globals.campaigns.active as Campaign[]).find(c => c.type === 'OFFER')
  );
  const loading = useSelector(
    (state: AppState) => state.areas.category.productList.loading as boolean
  );

  const currentPage = Math.floor(pageInfo.offset / pageInfo.limit) + 1;

  const generatePaginationUrl = useCallback(
    (pageNumber: number) =>
      `?${queryString.stringify({
        ...queryString.parse(search.substring(1)),
        offset: pageNumber > 1 ? (pageNumber - 1) * pageInfo.limit : undefined,
      })}`,
    [search, pageInfo?.limit]
  );

  const productListItems = nodes.map((product, index) => {
    const campaign =
      productsWithActiveCampaign &&
      product.id.toString() in productsWithActiveCampaign
        ? productsWithActiveCampaign[product.id.toString()]
        : null;

    return (
      <Product
        key={product.id}
        id={product.id}
        mode={mode}
        link={product.pathName}
        name={product.name}
        image={
          <Lazy offset={0}>
            <ProductImage
              name={product.name}
              src={product.media.first}
              fallback={fallbackImage}
            />
          </Lazy>
        }
        price={getPriceBySortMode(product, pageInfo.sort)}
        rating={
          showRatings
            ? product.aggregatedRating?.score ||
              product.userReviewSummary.rating
            : undefined
        }
        ratingCount={
          product.aggregatedRating?.count || product.userReviewSummary.count
        }
        category={showCategory ? product.category : undefined}
        campaignBadge={
          campaign && activeCampaign
            ? getBadge(campaign, activeCampaign, mode)
            : undefined
        }
        brand={product.brand}
        storesCount={product.priceSummary.count}
        storesCountText={t('campaigns', 'item.sold-by-n-stores', {
          n: product.priceSummary.count,
        })}
        properties={getSelectedProductProperties(
          product.properties && product.properties.nodes,
          selectedProperties
        )}
        selectable={showProductSelector}
        selected={
          showProductSelector &&
          Boolean(
            selectedProducts.find(
              selectedProduct => selectedProduct.id === product.id
            )
          )
        }
        onSelect={onProductSelect}
        onDeselect={onProductDeselect}
        onCategoryLinkClick={clickInfo =>
          trackInternalNavigationClick({
            scope:
              InternalNavigationScope.POPULAR_PRODUCTS_PRODUCT_CARD_CATEGORY_LINK,
            nextPage: clickInfo.url,
            index,
          })
        }
        onClick={() => {
          const offset = pageInfo.offset ? pageInfo.offset : 0;
          const clickIndex = index + 1 + offset;
          onProductClicked(clickIndex);
          trackInternalNavigationClick({
            scope: InternalNavigationScope.POPULAR_PRODUCTS_PRODUCT_CARD,
            nextPage: product.pathName,
            index,
          });
        }}
      />
    );
  });

  const advertSpacing = { top: '10px', bottom: '10px' };
  const ads = {
    desktopInline: (
      <AdContainer
        scalable
        scaleBase=".pj-ui-panel"
        scaleOrigin="left center"
        placement="panorama_extra"
        format="panorama_1_wde"
        min={861}
        max={10000}
        spacing={advertSpacing}
      />
    ),
    mobileInline1: (
      <AdContainer
        placement="dubbelpanorama1"
        format="panorama_1"
        min={0}
        max={860}
        spacing={advertSpacing}
      />
    ),
    mobileInline2: (
      <AdContainer
        placement="dubbelpanorama2"
        format="panorama_2"
        min={0}
        max={860}
        spacing={advertSpacing}
        triggerPoint={100}
      />
    ),
    mobileInline3: (
      <AdContainer
        placement="dubbelpanorama2"
        format="panorama_3"
        min={0}
        max={860}
        spacing={advertSpacing}
        triggerPoint={100}
      />
    ),
    ...adverts,
  };

  const adWrapper = React.useCallback(
    (ad: React.ReactElement, adClass: string) => (
      <ProductListingItem mode={mode} className={adClass}>
        {ad}
      </ProductListingItem>
    ),
    [mode]
  );

  return (
    <ScrollElement name={SCROLL_KEY} className={className}>
      <Panel data-test-type="product-listing">
        <>
          {header}
          <ProductListingWrapper $loading={loading} $mode={mode}>
            {mode !== 'table' ? (
              <>
                {adWrapper(ads.desktopInline, 'desktop-inline-ad')}
                {adWrapper(ads.mobileInline1, 'mobile-inline-ad-1')}

                {nodes.length > 9 &&
                  adWrapper(ads.mobileInline2, 'mobile-inline-ad-2')}
                {mode === 'grid' &&
                  nodes.length > 18 &&
                  adWrapper(ads.mobileInline3, 'mobile-inline-ad-3')}

                {feedbackPrompt && (
                  <ProductListingItem
                    mode={mode}
                    key="feedback"
                    style={{
                      order: feedbackPromptPosition,
                    }}
                  >
                    {feedbackPrompt}
                  </ProductListingItem>
                )}
                {productListItems.map((prod, index) => (
                  <ProductListingItem
                    mode={mode}
                    key={prod.key}
                    style={{
                      order: index,
                    }}
                  >
                    {prod}
                  </ProductListingItem>
                ))}
              </>
            ) : (
              <>
                <ProductListingHeader
                  firstColumnIndent={showProductSelector ? 2 : 1}
                  selectedProperties={selectedProperties}
                  onSortChange={onSortChange}
                  sortInfo={pageInfo.sort}
                />
                <ProductListingTableBody
                  $loading={loading}
                  data-test="ProductTable"
                >
                  {productListItems.slice(0, 2)}
                  {adWrapper(ads.mobileInline1, 'mobile-inline-ad-1')}
                  {adWrapper(ads.desktopInline, 'desktop-inline-ad')}
                  {productListItems.slice(2, 10)}
                  {productListItems.length > 9 &&
                    adWrapper(ads.mobileInline2, 'mobile-inline-ad-2')}
                  {productListItems.slice(10)}
                </ProductListingTableBody>
              </>
            )}
          </ProductListingWrapper>

          {children}
          <ProductListingFooter>
            {showPagination && (
              <ScrollLink
                to={SCROLL_KEY}
                shouldScroll={({ tagName }: HTMLElement) => tagName === 'A'}
                calculateOffset={addStickyOffset}
              >
                <NumberPagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(productCount / pageInfo.limit)}
                  linkToPage={generatePaginationUrl}
                />
              </ScrollLink>
            )}
            {market.showProductInformationTexts && nodes.length > 0 && (
              <ProductListingInformation>
                <Translate domain="category">
                  product-list.data-information
                </Translate>
              </ProductListingInformation>
            )}
          </ProductListingFooter>
        </>
      </Panel>
    </ScrollElement>
  );
};

export default memo(ProductListing);
